import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { multiCloudService } from '../../services';
import { CloudProvider, CloudConnection, SyncStatus } from '../../types/cloud';

// Re-export types with 'export type'
export type { CloudProvider, CloudConnection, SyncStatus };

interface AutoSyncConfigPayload {
  connectionId: string;
  autoSync: boolean;
  syncFrequency?: string;
  syncDirection?: string;
}

interface CloudIntegrationState {
  providers: CloudProvider[];
  connections: CloudConnection[];
  syncJobs: SyncStatus[];
  loading: boolean;
  error: string | null;
}

const initialState: CloudIntegrationState = {
  providers: [],
  connections: [],
  syncJobs: [],
  loading: false,
  error: null
};

export const fetchCloudProviders = createAsyncThunk(
  'cloudIntegration/fetchProviders',
  async () => {
    const response = await multiCloudService.getProviders();
    return response as CloudProvider[];
  }
);

export const fetchCloudConnections = createAsyncThunk(
  'cloudIntegration/fetchConnections',
  async () => {
    const response = await multiCloudService.getConnections();
    return response;
  }
);

export const disconnectCloudConnection = createAsyncThunk(
  'cloudIntegration/disconnectConnection',
  async (connectionId: string) => {
    await multiCloudService.disconnectConnection(connectionId);
    return connectionId;
  }
);

export const getAuthUrl = async (providerId: string) => {
  return await multiCloudService.getAuthUrl(providerId);
};

export const createSyncJob = createAsyncThunk(
  'cloudIntegration/createSyncJob',
  async (connectionId: string) => {
    const response = await multiCloudService.createSyncJob(connectionId);
    return response;
  }
);

export const fetchSyncJobs = createAsyncThunk(
  'cloudIntegration/fetchSyncJobs',
  async () => {
    const response = await multiCloudService.getSyncJobs();
    return response;
  }
);

export const cancelSyncJob = createAsyncThunk(
  'cloudIntegration/cancelSyncJob',
  async (jobId: string) => {
    await multiCloudService.cancelSyncJob(jobId);
    return jobId;
  }
);

export const updateAutoSyncSettings = createAsyncThunk(
  'cloudIntegration/updateAutoSyncSettings',
  async (payload: AutoSyncConfigPayload, { rejectWithValue }) => {
    try {
      const config = {
        autoSync: payload.autoSync,
        syncFrequency: payload.syncFrequency,
        syncDirection: payload.syncDirection,
      };
      const response = await multiCloudService.configureAutoSync(payload.connectionId, config);
      // Assuming the API returns the updated connection or relevant parts that match CloudConnection
      // Or, if it just returns success, we might need to merge payload into existing connection state
      // For now, let's assume response.data is the updated connection data or contains { id, syncEnabled, syncFrequency, syncDirection }
      return response; // Or response.data if the service doesn't strip it
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update auto-sync settings');
    }
  }
);

const cloudIntegrationSlice = createSlice({
  name: 'cloudIntegration',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCloudProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCloudProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.providers = action.payload;
      })
      .addCase(fetchCloudProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch providers';
      })
      .addCase(fetchCloudConnections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCloudConnections.fulfilled, (state, action) => {
        state.loading = false;
        state.connections = action.payload;
      })
      .addCase(fetchCloudConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch connections';
      })
      // Disconnect Cloud Connection
      .addCase(disconnectCloudConnection.pending, (state) => {
        state.loading = true; // Or a specific loading flag for the connection
      })
      .addCase(disconnectCloudConnection.fulfilled, (state, action) => {
        state.loading = false;
        state.connections = state.connections.filter(
          (connection) => connection.id !== action.payload
        );
      })
      .addCase(disconnectCloudConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to disconnect connection';
      })
      // Create Sync Job
      .addCase(createSyncJob.pending, (state) => {
        // Optionally set a loading state for this specific job/connection
      })
      .addCase(createSyncJob.fulfilled, (state, action) => {
        const index = state.syncJobs.findIndex(job => job.id === action.payload.id);
        if (index !== -1) {
          state.syncJobs[index] = action.payload;
        } else {
          state.syncJobs.push(action.payload);
        }
        // Update connection's lastSync or sync status
        const connection = state.connections.find(conn => conn.id === action.payload.connectionId);
        if (connection) {
          connection.lastSync = new Date().toISOString(); // Or use status from job
        }
      })
      .addCase(createSyncJob.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create sync job';
      })
      // Fetch Sync Jobs
      .addCase(fetchSyncJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSyncJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.syncJobs = action.payload;
      })
      .addCase(fetchSyncJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch sync jobs';
      })
      // Cancel Sync Job
      .addCase(cancelSyncJob.pending, (state) => {
        // Optionally set a loading state for this specific job
      })
      .addCase(cancelSyncJob.fulfilled, (state, action) => {
        const job = state.syncJobs.find(j => j.id === action.payload);
        if (job) {
          job.status = 'canceled'; // Or remove it: state.syncJobs = state.syncJobs.filter(j => j.id !== action.payload);
        }
      })
      .addCase(cancelSyncJob.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to cancel sync job';
      })
      // Update Auto-Sync Settings
      .addCase(updateAutoSyncSettings.pending, (state) => {
        // Optionally set a specific loading flag for the connection being configured
      })
      .addCase(updateAutoSyncSettings.fulfilled, (state, action) => {
        // action.payload should be the response from configureAutoSync
        // Assuming it returns the updated connection details or at least the ID and new settings
        const updatedConnectionData = action.payload; // Adjust if API returns data differently
        const connection = state.connections.find(conn => conn.id === updatedConnectionData.id);
        if (connection) {
          connection.syncEnabled = updatedConnectionData.syncEnabled;
          connection.syncFrequency = updatedConnectionData.syncFrequency;
          connection.syncDirection = updatedConnectionData.syncDirection;
          // Potentially update other fields if returned by the API
        }
      })
      .addCase(updateAutoSyncSettings.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to update auto-sync settings';
      });
  }
});

export default cloudIntegrationSlice.reducer; 